import { useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import Button from '@/design_system/Button';
import { Footer, FooterButtons, FooterError } from '@/design_system/Footer';
import IconCancel from '@/icons/Cancel.svg';
import {
  useConfirmExchangeStockAvailability,
  useRefuseExchangeStockAvailability,
} from '@/models/article';
import { useRequestContext } from '@/routes/Requests/contexts/RequestContext';

import { ArticleActionProps } from './ArticleActions';

export const ConfirmExchangeStockAvailabilityAction = ({
  article,
  onActionDone,
}: ArticleActionProps) => {
  const { request } = useRequestContext();
  const { t } = useLingui();
  const [apiError, setApiError] = useState('');

  const {
    mutateAsync: confirmExchangeStockAvailability,
    isPending: isPendingConfirmExchangeStockAvailability,
  } = useConfirmExchangeStockAvailability({
    articleId: article.id,
  });

  const {
    mutateAsync: refuseExchangeStockAvailability,
    isPending: isPendingRefuseExchangeStockAvailability,
  } = useRefuseExchangeStockAvailability({
    articleId: article.id,
  });

  const handleConfirm = async () => {
    setApiError('');

    try {
      await confirmExchangeStockAvailability();
      onActionDone();
    } catch (err: any) {
      console.error(err);
      setApiError(
        (err.message as string) ?? t({ id: '_general.error.unknown', message: 'Unknown error' })
      );
    }
  };

  const handleRefuse = async () => {
    setApiError('');

    try {
      await refuseExchangeStockAvailability();

      // If the stock location is refusing the only item of the request, after the refusal it won't
      // be able to see the request anymore.
      onActionDone({
        navigateToRequests: request.articles.length === 1 && !!article.taskType,
      });
    } catch (err: any) {
      console.error(err);
      setApiError(
        (err.message as string) ?? t({ id: '_general.error.unknown', message: 'Unknown error' })
      );
    }
  };

  return (
    <Footer>
      {!!apiError && <FooterError>{apiError}</FooterError>}
      <FooterButtons>
        <Button
          variant="secondary-danger"
          size="medium"
          onPress={handleRefuse}
          disabled={isPendingConfirmExchangeStockAvailability}
          isLoading={isPendingRefuseExchangeStockAvailability}
        >
          <IconCancel />
          <Trans id="article.actions.refuse_exchange_stock_availability">
            Report as out of stock
          </Trans>
        </Button>
        <Button
          variant="primary"
          size="medium"
          onPress={handleConfirm}
          disabled={isPendingRefuseExchangeStockAvailability}
          isLoading={isPendingConfirmExchangeStockAvailability}
        >
          <Trans id="article.actions.confirm_exchange_stock_availability.validate">
            Confirm stock availability
          </Trans>
        </Button>
      </FooterButtons>
    </Footer>
  );
};
